import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import AdvertBanner from "@/components/AdvertBanner";
import Footer from "@/components/Footer";
import GbetBanner from "@/components/GbetBanner";
import axios from "axios";
import { Clock, ChevronRight, ShoppingCart, Layers } from "lucide-react";
import SEO from "@/components/SEO";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PoolDrawsPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/posts?category=pool_draws`)
      .then(r => setPosts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("/api")) return `${BACKEND}${url}`;
    return url;
  };

  const stripHtml = (html) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC]"><div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-green-600"></div></div>;

  return (
    <div className="min-h-screen bg-[#FAFBFC]" data-testid="pool-draws-page">
      <Navbar />
      <div className="pt-28 pb-16 px-4">
        <AdvertBanner />
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="bg-emerald-100 text-emerald-700 mb-3">Pool Draws</Badge>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Pool Draws</h1>
            <p className="text-gray-500 max-w-xl mx-auto">Expert football pool draw predictions and analysis</p>
          </div>

          {posts.length === 0 ? (
            <div className="text-center py-20">
              <Layers className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 mb-4">No pool draws posted yet. Check back soon!</p>
              <Button onClick={() => navigate("/buy")} className="bg-green-600 hover:bg-green-700 text-white rounded-full px-6" data-testid="empty-buy-btn">
                Buy Fixed Draws
              </Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map(post => {
                const color = post.coupon_color || "brown";
                return (
                <Card key={post.post_id} className={`bg-white border-gray-200 overflow-hidden group hover:shadow-lg transition-shadow coupon-border-${color}`} data-testid={`draws-post-${post.post_id}`}>
                  {post.image_url && (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={getImageUrl(post.image_url)}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <Badge className="cat-pool_draws text-xs">Pool Draws</Badge>
                      {post.week_number && <Badge className={`coupon-${color} text-xs`}>Week {post.week_number}</Badge>}
                      {post.season && <Badge variant="outline" className="text-xs text-gray-700 border-gray-300">{post.season} Pools</Badge>}
                      <span className="text-xs text-gray-500 flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </span>
                    </div>
                    <h3 className="font-bold text-gray-900 text-lg mb-2 line-clamp-2">{post.title}</h3>
                    <p className="text-sm text-gray-500 line-clamp-3 mb-4">{stripHtml(post.content)}</p>
                    <div className="flex items-center gap-2">
                      <Link to={`/post/${post.post_id}`} className="text-sm text-green-600 font-medium flex items-center hover:underline" data-testid={`read-more-${post.post_id}`}>
                        Read More <ChevronRight className="h-4 w-4 ml-0.5" />
                      </Link>
                    </div>
                    <Button
                      onClick={() => navigate("/buy")}
                      className="w-full mt-4 bg-amber-500 hover:bg-amber-600 text-gray-900 font-bold rounded-full"
                      data-testid={`buy-btn-${post.post_id}`}
                    >
                      <ShoppingCart className="h-4 w-4 mr-2" /> Buy Fixed Games
                    </Button>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className="px-4"><div className="max-w-5xl mx-auto"><GbetBanner /></div></div>
      <Footer />
    </div>
  );
};

export default PoolDrawsPage;
